/**
 * Finnish catalogue.
 *
 * Notes on the wording chosen here:
 *   - "huone" for room, "ylläpitäjä" for the root admin, "operaattori" for
 *     operators, "kirjautunut" for a registered user, "anonyymi" for guests.
 *   - Buttons use short imperative forms; hints use the passive where Finnish
 *     would rather not name the actor.
 *   - Room names keep the # prefix, and inflected room references use the
 *     "#huone-huoneessa" style only where it reads naturally; elsewhere the
 *     sentence is reshaped so the name needs no case ending, since we cannot
 *     inflect arbitrary user-chosen names.
 */
export default {
  // ---------------------------------------------------------------- gate
  'ui.tagline': 'Live-huoneet äänellä ja videolla',
  'ui.tab.guest': 'Liity anonyymisti',
  'ui.tab.login': 'Kirjaudu',
  'ui.tab.register': 'Rekisteröidy',
  'ui.field.nickname': 'Nimimerkki',
  'ui.field.nicknamePlaceholder': 'valitse vapaa nimimerkki',
  'ui.field.room': 'Huone',
  'ui.field.roomPlaceholder': 'palvelimen oletushuone',
  'ui.field.password': 'Salasana',
  'ui.field.repeatPassword': 'Salasana uudelleen',
  'ui.field.nicknameToClaim': 'Varattava nimimerkki',
  'ui.gate.guestHint':
    'Rekisteröidyt nimimerkit on suojattu — niitä ei voi käyttää ilman salasanaa.',
  'ui.gate.registerHint':
    'Rekisteröityminen varaa nimimerkin pysyvästi — anonyymit käyttäjät eivät voi käyttää sitä.',
  'ui.gate.keepSignedIn': 'Pidä minut kirjautuneena',
  'ui.gate.joinChat': 'Liity keskusteluun',
  'ui.gate.signIn': 'Kirjaudu sisään',
  'ui.gate.createAccount': 'Luo tunnus',
  'ui.gate.language': 'Kieli',
  'ui.gate.checkingBrowser': 'Tarkistetaan selainta…',
  'ui.gate.joining': 'Liitytään…',
  'ui.gate.connecting': 'Yhdistetään…',
  'ui.gate.restoring': 'Palautetaan istuntoa…',
  'ui.gate.signedOut': 'Kirjauduttu ulos.',
  'ui.gate.passwordMismatch': 'Salasanat eivät täsmää.',
  'ui.gate.nicknameAvailable': 'Nimimerkki "{nickname}" on vapaa.',
  'ui.gate.nicknameUnavailable': 'Nimimerkki ei ole käytettävissä.',

  // ---------------------------------------------------------------- chrome
  'ui.brand': 'weppis',
  'ui.signOut': 'Kirjaudu ulos',
  'ui.userMenu': 'Valinnat',
  'ui.support': 'Tue minua Ko-fissa',
  'ui.mentionHint': 'Kirjoita käyttäjälle {nickname}',
  'ui.stage.above': 'Näytä kamerat keskustelun yläpuolella',
  'ui.stage.beside': 'Näytä kamerat keskustelun vierellä',
  'ui.stage.resize': 'Vedä muuttaaksesi kameroiden kokoa — kaksoisnapsautus palauttaa',
  'ui.account': 'Tunnus',
  'ui.account.title': 'Tunnuksen asetukset',
  'ui.account.nickname': 'Nimimerkki',
  'ui.account.nicknameHint': 'Tällä myös kirjaudut sisään. Vaihtaminen vapauttaa vanhan nimimerkin muiden käyttöön.',
  'ui.account.currentPassword': 'Nykyinen salasana',
  'ui.account.newPassword': 'Uusi salasana',
  'ui.account.password': 'Salasana',
  'ui.account.changeNickname': 'Vaihda nimimerkki',
  'ui.account.changePassword': 'Vaihda salasana',
  'ui.account.nicknameChanged': 'Olet nyt {nickname}.',
  'ui.account.passwordChanged': 'Salasana vaihdettu.',
  'ui.theme': 'Ulkoasu',
  'ui.theme.system': 'Järjestelmän mukaan',
  'ui.theme.light': 'Vaalea',
  'ui.theme.dark': 'Tumma',
  'ui.theme.systemHint': 'Järjestelmäsi asetus on nyt {theme}.',
  'ui.theme.fluid': 'Nestetausta',
  'ui.theme.fluidHint':
    'Neste, jota voi pyörittää keskustelun takana. Tekee viesteistä hieman vaikealukuisempia.',
  'ui.admin': 'Ylläpito',
  'ui.rooms': 'Huoneet',
  'ui.dm.heading': 'Yksityisviestit',
  'ui.dm.itemLabel': 'Keskustelu käyttäjän {nickname} kanssa',
  'ui.dm.unreadLabel': '{count} lukematonta',
  'ui.dm.closeTitle': 'Sulje keskustelu käyttäjän {nickname} kanssa',
  'ui.dm.subtitle': 'Vain te kaksi näette tämän.',
  'ui.dm.empty': 'Ei vielä viestejä. Kirjoita käyttäjälle {nickname} alla.',
  'ui.dm.placeholder': 'Viesti käyttäjälle {nickname}',
  'ui.members': 'Jäsenet',
  'ui.membersIn': 'Huoneessa #{room}',
  'ui.newRoom': 'Liity huoneeseen tai luo huone',
  'ui.showSidebar': 'Näytä huoneet ja jäsenet',
  'ui.hideSidebar': 'Piilota huoneet ja jäsenet',
  'ui.roomActions': 'Huoneen toiminnot',
  'ui.noRoom': 'ei huonetta',
  'ui.noTopic': 'Ei aihetta.',
  'ui.joinToStart': 'Liity huoneeseen aloittaaksesi.',
  'ui.joinFromSidebar': 'Liity huoneeseen sivupalkista aloittaaksesi.',
  'ui.hiddenRoomSuffix': 'piilotettu huone',
  'ui.badge.hidden': 'piilotettu',
  'ui.badge.hiddenByYou': 'vaiennettu',
  'ui.badge.anonymous': 'anonyymi',
  'ui.role.root': 'ylläpitäjä',
  'ui.role.operator': 'operaattori',
  'ui.role.user': 'kirjautunut',
  'ui.role.guest': 'anonyymi',

  // ---------------------------------------------------------------- composer
  'ui.mic': 'Mikki',
  'ui.mute': 'Mykistä',
  'ui.unmute': 'Poista mykistys',
  'ui.talk': "Pid\u00e4 puhuaksesi",
  'ui.talk.hint': "Pid\u00e4 pohjassa avataksesi mikrofonin",
  'ui.talk.live': "Puhut \u2014 vapauta mykist\u00e4\u00e4ksesi",
  'ui.settings.notifications': 'Ilmoitukset',
  'ui.settings.notificationSound': 'Soita äänimerkki maininnoista ja yksityisviesteistä',
  'ui.settings.notificationSoundHint':
    'Lyhyt äänimerkki, kun joku mainitsee nimesi tai lähettää sinulle yksityisviestin. Se käyttää tässä valittua kaiutinta ja äänenvoimakkuutta, ja vaikenee kun kaikki on mykistetty.',
  'ui.settings.talking': "Puhuminen",
  'ui.settings.pushToTalk': "Pid\u00e4 puhuaksesi",
  'ui.settings.pushToTalkHint': "Mikrofoni pysyy mykistettyn\u00e4 ja avautuu vain, kun pid\u00e4t Puhu-painiketta pohjassa. Se korvaa mykistyspainikkeen.",
  'ui.camera': 'Kamera',
  'ui.settings': 'Asetukset',
  'ui.emotes': 'Emojit',
  'ui.attach.title': 'Liitä kuva tai video',
  'ui.attach.chooseFile': 'Valitse tiedosto',
  'ui.attach.takePhoto': 'Ota kuva',
  'ui.attach.recordVideo': 'Kuvaa video',
  'ui.attach.uploading': 'Lähetetään {name}… {percent} %',
  'ui.attach.tooMany': 'Yksi tiedosto kerrallaan.',
  'ui.attach.openInNewTab': 'Avaa {name} uudessa välilehdessä',
  'ui.attach.video': 'Video',
  'ui.attach.expired': 'Liite on vanhentunut.',
  'ui.attach.image': 'Kuva',
  'ui.send': 'Lähetä',
  'ui.messagePlaceholder': 'Viesti, tai /help komennoille',
  'ui.messagePlaceholderMax': 'Viesti (enintään {max}), tai /help komennoille',
  'ui.messagePlaceholderOffline': 'Yhdistetään uudelleen — viestejä ei voi lähettää juuri nyt',
  'ui.mic.start': 'Aloita äänen lähetys',
  'ui.mic.stop': 'Lopeta äänen lähetys',
  'ui.mute.on': 'Mykistä mikrofoni (pysy puhelussa)',
  'ui.mute.off': 'Poista mikrofonin mykistys',
  'ui.mute.needsMic': 'Kytke mikrofoni ensin päälle',
  'ui.camera.on': 'Kytke kamera ja mikrofoni päälle',
  'ui.camera.off': 'Sammuta kamera (ja sen mikrofoni)',
  'ui.media.unavailable': 'Ääni ja video eivät ole käytettävissä täällä',

  // ---------------------------------------------------------------- connection
  'ui.conn.open': 'yhdistetty',
  'ui.conn.connecting': 'yhdistetään…',
  'ui.conn.reconnecting': 'yhdistetään uudelleen…',
  'ui.conn.closed': 'katkaistu',
  'ui.conn.lostRetryIn': 'Yhteys katkesi. Yhdistetään uudelleen {seconds} s kuluttua…',
  'ui.conn.lostRetrying': 'Yhteys katkesi. Yhdistetään uudelleen…',
  'ui.conn.connectingBanner': 'Yhdistetään…',
  'ui.conn.offline': 'Ei yhteyttä.',
  'ui.conn.retryNow': 'Yritä uudelleen',

  'ui.update.available': 'Chatista on uusi versio. Päivitä sivu ottaaksesi sen käyttöön.',
  'ui.update.refresh': 'Päivitä',
  'ui.update.dismiss': 'Ei nyt',

  // ---------------------------------------------------------------- dialogs
  'ui.cancel': 'Peruuta',
  'ui.ok': 'OK',
  'ui.done': 'Valmis',
  'ui.close': 'Sulje',
  'ui.save': 'Tallenna',
  'ui.newRoom.title': 'Liity huoneeseen tai luo huone',
  'ui.newRoom.name': 'Huoneen nimi',
  'ui.newRoom.namePlaceholder': 'huoneen nimi',
  'ui.newRoom.join': 'Liity',
  'ui.newRoom.hidden': 'Piilotettu huone (ei näy huonelistassa)',
  'ui.newRoom.hiddenHint':
    'Piilotus vaikuttaa vain jos huone luodaan nyt. Huoneeseen voi silti liittyä, jos sen nimen tietää.',
  'ui.newRoom.nameRequired': 'Anna huoneen nimi.',

  'ui.room.summary': '#{room} — {count} paikalla.',
  'ui.room.topicIs': 'Aihe: {topic}',
  'ui.room.noTopicSet': 'Ei aihetta.',
  'ui.room.isHidden': 'Piilotettu: ei näy huonelistassa.',
  'ui.room.isPersistent': 'Tämä huone on pysyvä eikä sitä voi poistaa.',
  'ui.room.setTopic': 'Aseta aihe…',
  'ui.room.leave': 'Poistu huoneesta',
  'ui.room.remove': 'Poista huone…',
  'ui.room.hide': 'Piilota huonelistasta',
  'ui.room.show': 'Näytä huonelistassa',
  'ui.room.onlyOperatorsRemove': 'Vain operaattorit voivat poistaa huoneen.',
  'ui.room.topicTitle': 'Huoneen #{room} aihe',
  'ui.room.topicLabel': 'Aihe',
  'ui.room.topicPlaceholder': 'Mistä tässä huoneessa keskustellaan?',
  'ui.room.topicClearHint': 'Jätä tyhjäksi poistaaksesi aiheen.',
  'ui.room.removeTitle': 'Poistetaanko #{room}?',
  'ui.room.removeWarning':
    'Huoneen #{room} poistaminen katkaisee {count} jäsenen yhteyden ja poistaa sen historian. Tätä ei voi peruuttaa.',
  'ui.room.removeConfirmLabel': 'Kirjoita huoneen nimi vahvistukseksi',
  'ui.room.removeConfirmButton': 'Poista huone',
  'ui.room.removeMismatch': 'Kirjoita "{room}" vahvistukseksi.',
  'ui.room.leaveTitle': 'Poistu huoneesta #{room}',

  // ---------------------------------------------------------------- jukeboksi
  'ui.jukebox': 'Jukeboksi',
  'ui.jukebox.start': 'Käynnistä jukeboksi',
  'ui.jukebox.stop': 'Sammuta jukeboksi',
  'ui.jukebox.hide': 'Piilota huoneen jukeboksi — youtube.comilta ei pyydetä mitään',
  'ui.jukebox.show': 'Näytä ja kuule huoneen jukeboksi',
  'ui.jukebox.play': 'Toista',
  'ui.jukebox.pause': 'Keskeytä',
  'ui.jukebox.skip': 'Seuraava kappale',
  'ui.jukebox.add': 'Lisää video',
  'ui.jukebox.remove': 'Poista jonosta',
  'ui.jukebox.volume': 'Jukeboksin äänenvoimakkuus',
  'ui.jukebox.mute': 'Mykistä jukeboksi',
  'ui.jukebox.unmute': 'Poista jukeboksin mykistys',
  'ui.jukebox.resize': 'Vedä muuttaaksesi kokoa — kaksoisnapsauta tasaa',
  'ui.jukebox.nothingPlaying': 'Ei mitään soimassa',
  'ui.jukebox.addSomething': 'Lisää jonoon +-painikkeella',
  'ui.jukebox.queuedBy': 'lisännyt {nickname}',
  'ui.jukebox.queueCount': 'Jono · {count}',
  'ui.jukebox.queueEmpty': 'Jono on tyhjä.',
  'ui.jukebox.addTitle': 'Lisää jonoon',
  'ui.jukebox.link': 'YouTube-linkki',
  'ui.jukebox.addHint':
    'Videon osoite tai sen tunnus. Kaikki huoneessa kuulevat sen samaan aikaan.',
  'ui.jukebox.addConfirm': 'Lisää jonoon',
  'ui.jukebox.linkRequired': 'Liitä YouTube-linkki.',
  'ui.jukebox.queued': '"{title}" lisättiin jonoon.',
  'ui.jukebox.playerFailed': 'YouTube-soitinta ei voitu ladata.',

  'ui.member.summary': '{nickname} — {kind}{role}.',
  'ui.member.guest': 'anonyymi vieras',
  'ui.member.registered': 'rekisteröity tunnus',
  'ui.member.roleRoot': ', ylläpitäjä',
  'ui.member.roleOperator': ', operaattori',
  'ui.member.onDevices': ', {count} laitteella',
  'ui.member.hiddenFromOthers': ' — piilotettu muilta',
  'ui.member.activateForActions': '{description}. Avaa toiminnot.',
  'ui.member.privateMessage': 'Yksityisviesti',
  'ui.member.hide': 'Piilota {nickname}',
  'ui.member.unhide': 'Näytä {nickname} taas',
  'ui.member.isHiddenByYou': 'Olet piilottanut hänet.',
  'ui.hidden.manage': 'Piilotetut',
  'ui.hidden.title': 'Piilottamasi käyttäjät',
  'ui.hidden.summary':
    'Heidän viestinsä eivät näy chatissasi. Tämä on vain sinun asetuksesi — kenellekään ei kerrota, ja se koskee kaikkia huoneita tässä selaimessa. Piilotettuja: {count}.',
  'ui.hidden.empty':
    'Et ole piilottanut ketään. Valitse käyttäjä jäsenlistasta piilottaaksesi hänet; hänen viestinsä katoavat chatistasi, eikä se vaikuta muihin.',
  'ui.hidden.unhide': 'Näytä',
  'ui.hidden.clear': 'Näytä kaikki taas',

  'ui.member.whois': 'Tiedot',
  'ui.member.stopMic': 'Sammuta mikrofoni',
  'ui.member.stopVideo': 'Sammuta video',
  'ui.member.kick': 'Potkaise',
  'ui.member.ban': 'Anna porttikielto…',
  'ui.member.makeOperator': 'Tee operaattoriksi',
  'ui.member.removeOperator': 'Poista operaattorius',

  'ui.kick.title': 'Potkaise {nickname}',
  'ui.kick.scope': 'Laajuus',
  'ui.kick.fromRoom': 'Huoneesta #{room}',
  'ui.kick.fromServer': 'Katkaise yhteys palvelimeen',
  'ui.kick.reason': 'Syy',
  'ui.kick.reasonPlaceholder': 'valinnainen syy',
  'ui.kick.hint':
    'Potkaisu on väliaikainen — käyttäjä voi liittyä heti uudelleen. Hänen viestinsä poistetaan.',
  'ui.kick.confirm': 'Potkaise',

  'ui.ban.title': 'Porttikielto: {nickname}',
  'ui.ban.fromRoom': 'Huoneesta #{room}',
  'ui.ban.fromServer': 'Koko palvelimelta',
  'ui.ban.duration': 'Kesto',
  'ui.ban.durationPlaceholder': 'esim. 30m, 2h, 7d — tyhjä = toistaiseksi',
  'ui.ban.timed': 'Määräaikainen porttikielto: {duration}.',
  'ui.ban.permanentWarning': 'Kestoa ei annettu — porttikielto on PYSYVÄ.',
  'ui.ban.invalidDuration':
    '"{value}" ei ole kesto. Käytä muotoa 30m, 2h, 7d tai minuuttimäärää, tai jätä tyhjäksi pysyvää porttikieltoa varten.',
  'ui.ban.confirm': 'Anna porttikielto',

  'ui.operator.grantTitle': 'Tee operaattoriksi: {nickname}',
  'ui.operator.revokeTitle': 'Poista operaattorius: {nickname}',
  'ui.operator.scope': 'Laajuus',
  'ui.operator.scopePlaceholder': 'tyhjä = koko palvelin, tai #huone',
  'ui.operator.grantHint':
    'Anna käyttäjälle {nickname} operaattorin oikeudet: hän voi potkaista ja antaa porttikieltoja. Jätä laajuus tyhjäksi koko palvelimen osalta.',
  'ui.operator.revokeHint':
    'Poista käyttäjän {nickname} operaattorin oikeudet. Jätä laajuus tyhjäksi poistaaksesi oikeudet koko palvelimelta.',
  'ui.operator.grant': 'Anna oikeudet',
  'ui.operator.revoke': 'Poista',

  // ---------------------------------------------------------------- settings
  'ui.settings.title': 'Ääni- ja videoasetukset',
  'ui.settings.microphone': 'Mikrofoni',
  'ui.settings.camera': 'Kamera',
  'ui.settings.speakers': 'Kaiuttimet',
  'ui.settings.systemDefault': 'Järjestelmän oletus',
  'ui.settings.volume': 'Saapuvan äänen ja videon voimakkuus',
  'ui.settings.volumeHint':
    'Koskee kaikkia huoneessa sekä jukeboksia. Jokaisella osallistujalla on myös oma säädin videoruudussaan, ja jukeboksilla omansa.',
  'ui.settings.muteAll': 'Mykistä kaikki kuulemasi',
  'ui.settings.unmuteAll': 'Poista mykistys',
  'ui.settings.muted': 'mykistetty',
  'ui.settings.quality': 'Kameran laatu',
  'ui.settings.quality360': '360p — pienin kaistankäyttö',
  'ui.settings.quality480': '480p',
  'ui.settings.quality720': '720p — suositus',
  'ui.settings.quality1080': '1080p — suurin kaistankäyttö',
  'ui.settings.shareScreen': 'Jaa näyttö',
  'ui.settings.stopSharing': 'Lopeta jakaminen',
  'ui.settings.testMic': 'Testaa mikrofoni',
  'ui.settings.stopTest': 'Lopeta testi',
  'ui.settings.language': 'Kieli',
  'ui.settings.deviceAccess': 'Salli laitteiden luettelointi (mikrofonit, kamerat ja kaiuttimet)',
  'ui.settings.loadingDevices': 'Ladataan laitteita…',
  'ui.settings.needsAccess':
    'Selain piilottaa laitteiden nimet ja kaiutinvalinnat, kunnes olet antanut sille luvan kerran. Sinun ei tarvitse lähettää mitään.',
  'ui.settings.noOutputChoice':
    'Tämä selain ei tue toistolaitteen valintaa; se käyttää järjestelmän oletusta.',
  'ui.settings.accessRefused':
    'Lupa evättiin, joten selain pitää laitelistan piilotettuna. Toisto käyttää järjestelmän oletusta kunnes annat luvan.',
  'ui.settings.micFailed': 'Mikrofonia ei voitu avata: {error}',
  'ui.settings.volumeFor': 'Käyttäjän {nickname} äänenvoimakkuus',
  'ui.settings.muteFeed': 'Mykistä {nickname}',
  'ui.settings.unmuteFeed': 'Poista mykistys: {nickname}',
  'ui.tile.fullscreen': 'Koko näyttö',
  'ui.tile.hideVideo': 'Piilota käyttäjän {nickname} video',
  'ui.tile.showVideo': 'Näytä käyttäjän {nickname} video',
  'ui.newMessage': '1 uusi viesti ↓',
  'ui.newMessages': '{count} uutta viestiä ↓',
  'ui.tile.soundBlocked': '🔇 Napsauta kuullaksesi',
  'ui.tile.playBlocked': '▶ Napsauta käynnistääksesi',

  // ---------------------------------------------------------------- admin
  'ui.admin.title': 'Ylläpito',
  'ui.admin.operators': 'Operaattorit',
  'ui.admin.bans': 'Porttikiellot',
  'ui.admin.audit': 'Tapahtumaloki',
  'ui.admin.announce': 'Tiedote',
  'ui.admin.loading': 'Ladataan…',
  'ui.admin.who': 'Kuka',
  'ui.admin.scope': 'Laajuus',
  'ui.admin.status': 'Tila',
  'ui.admin.online': 'paikalla',
  'ui.admin.offline': 'poissa',

  // ---- käyttäjät (vain root)
  'ui.admin.users': 'Käyttäjät',
  'ui.admin.usersHint':
    'Kaikki palvelimen rekisteröidyt tunnukset. Tunnuksen poisto on lopullinen: se kirjaa tunnuksen ulos kaikkialta, poistaa sen viestit ja vapauttaa nimimerkin.',
  'ui.admin.userOnline': { one: 'paikalla', other: 'paikalla ({count} laitetta)' },
  'ui.admin.userDisabled': 'käytöstä poistettu',
  'ui.admin.userCreated': 'Rekisteröity',
  'ui.admin.userLastSignIn': 'Viimeksi kirjautunut',
  'ui.admin.userNeverSignedIn': 'ei koskaan',
  'ui.admin.userRemove': 'Poista',
  'ui.admin.userRemoveTitle': 'Poistetaanko {nickname}?',
  'ui.admin.userRemoveWarning':
    'Tämä poistaa tunnuksen "{nickname}" lopullisesti. Kaikki sen sanomat viestit poistetaan joka huoneesta, operaattorioikeudet menevät mukana, ja jokainen laite joka pitää sen istuntoa kirjataan ulos. Tätä ei voi peruuttaa.',
  'ui.admin.userRemoveWarningOnline': {
    one: '"{nickname}" on juuri nyt paikalla ja yhteys katkaistaan. Kaikki tunnuksen viestit poistetaan joka huoneesta, operaattorioikeudet menevät mukana, ja tunnus kirjataan ulos. Tätä ei voi peruuttaa.',
    other:
      '"{nickname}" on juuri nyt paikalla {count} laitteella ja kaikkien yhteys katkaistaan. Kaikki tunnuksen viestit poistetaan joka huoneesta, operaattorioikeudet menevät mukana, ja tunnus kirjataan ulos. Tätä ei voi peruuttaa.',
  },
  'ui.admin.userRemoveNickname':
    'Nimimerkki "{nickname}" vapautuu kenen tahansa käyttöön, myös nimettömille vierailijoille. Varaa se ensin komennolla /reserve {nickname}, jos haluat pitää sen poissa käytöstä.',
  'ui.admin.userRemoveConfirmLabel': 'Kirjoita nimimerkki vahvistukseksi',
  'ui.admin.userRemoveMismatch': 'Tämä ei vastaa nimimerkkiä "{nickname}".',
  'ui.admin.hidden': 'piilotettu',
  'ui.admin.serverWide': 'koko palvelin',
  'ui.admin.serverWideRoot': 'koko palvelin (ylläpitäjä)',
  'ui.admin.grantOperator': 'Anna operaattorin oikeudet',
  'ui.admin.revoke': 'Poista oikeudet',
  'ui.admin.rootOnlyOperators': 'Vain ylläpitäjä voi muuttaa operaattorin oikeuksia.',
  'ui.admin.noOperators': 'Operaattoreita ei ole vielä nimitetty.',
  'ui.admin.noBans': 'Ei voimassa olevia porttikieltoja.',
  'ui.admin.banId': 'Tunnus',
  'ui.admin.banType': 'Tyyppi',
  'ui.admin.banSubject': 'Kohde',
  'ui.admin.banExpires': 'Päättyy',
  'ui.admin.banBy': 'Antaja',
  'ui.admin.banReason': 'Syy',
  'ui.admin.banIndefinite': 'toistaiseksi',
  'ui.admin.lift': 'Peru',
  'ui.admin.auditEmpty': 'Tapahtumaloki on tyhjä.',
  'ui.admin.announcement': 'Tiedote',
  'ui.admin.announcePlaceholder': 'Lähetettävä viesti (enintään 500)',
  'ui.admin.announceTarget': 'Kohde',
  'ui.admin.announceTargetPlaceholder': 'tyhjä = kaikki palvelimen huoneet, tai #huone',
  'ui.admin.sendAnnouncement': 'Lähetä tiedote',

  // ---------------------------------------------------------------- notices
  'notice.reconnected': 'Yhteys palautui.',
  'notice.disconnected': 'Yhteys palvelimeen katkesi. Lataa sivu uudelleen yhdistääksesi.',
  'notice.joinRoomFirst': 'Liity ensin huoneeseen.',
  'notice.micMuted': 'Mikrofoni mykistetty.',
  'notice.micUnmuted': 'Mikrofonin mykistys poistettu.',
  'notice.micOff': 'Mikrofoni pois — et enää lähetä ääntä.',
  'notice.micChangeFailed': 'Mikrofonin vaihto ei onnistunut: {error}',
  'notice.nowKnownAs': 'Olet nyt {nickname}.',
  'notice.joined': {
    one: '{nickname} liittyi.',
    other: '{nickname} liittyi.',
  },
  'notice.joinedGuest': '{nickname} (anonyymi) liittyi.',
  'notice.left': '{nickname} poistui.',
  'notice.leftKicked': '{nickname} poistui (potkaisi {actor}).',
  'notice.leftBanned': '{nickname} poistui (porttikielto: {actor}).',
  'notice.renamed': '{from} on nyt {to}.',
  'notice.topicSet': '{actor} asetti aiheen: {topic}',
  'notice.topicCleared': '{actor} poisti aiheen.',
  'notice.mediaEnabled': '{actor} otti äänen ja videon käyttöön tässä huoneessa.',
  'notice.mediaDisabled': '{actor} poisti äänen ja videon käytöstä tässä huoneessa.',
  'notice.youWereKicked': '{actor} potkaisi sinut huoneesta #{room}{reason}.',
  'notice.youWereBanned': '{actor} antoi sinulle porttikiellon huoneeseen #{room} {until}{reason}.',
  'notice.youWereDisconnected': '{actor} katkaisi yhteytesi{reason}.',
  'notice.youWereBannedServer': '{actor} antoi sinulle porttikiellon palvelimelle {until}{reason}.',
  'notice.roomClosed': '{actor} sulki huoneen #{room}.',
  'notice.roomRemoved': '{actor} poisti huoneen #{room}.',
  'notice.userHidden': '{nickname} on piilotettu. Hänen viestinsä eivät näy chatissasi.',
  'notice.userUnhidden': '{nickname} ei ole enää piilotettu.',
  'notice.roomNowHidden': 'Huone #{room} on nyt piilotettu — se ei näy huonelistassa.',
  'notice.roomNowListed': 'Huone #{room} näkyy jälleen listassa.',
  'notice.messagesRemoved': 'Käyttäjän {nickname} viestit poistettiin{by}.',
  'notice.messagesRemovedBy': ' ({actor})',
  'notice.forceStopped': 'Operaattori sammutti: {kinds}.',
  'notice.jukeboxOpened': '{actor} käynnisti jukeboksin.',
  'notice.jukeboxClosed': '{actor} sammutti jukeboksin.',
  'notice.jukeboxUnplayable': '"{title}" ei toistunut, joten se ohitettiin.',
  'notice.mediaReset':
    'Mediapalvelin käynnistyi uudelleen. Kytke kamera tai mikrofoni uudelleen jatkaaksesi.',
  'notice.cameraWithoutMic':
    'Kamera on päällä, mutta mikrofonia ei voitu avata. Tarkista, että sekä selain että järjestelmä sallivat mikrofonin käytön.',
  'notice.kicked': 'Potkaistiin {nickname}{scope}{messages}.',
  'notice.kickedFromRoom': ' huoneesta #{room}',
  'notice.kickedFromServer': ' palvelimelta',
  'notice.messagesAlsoRemoved': ', poistettiin {count} viesti(ä)',
  'notice.operatorGranted': '{nickname} on nyt operaattori {scope}.',
  'notice.operatorRevoked': '{nickname} ei ole enää operaattori {scope}.',
  'notice.scopeServerWide': 'koko palvelimella',
  'notice.scopeRoom': 'huoneessa #{room}',
  'notice.announcementSent': 'Tiedote lähetettiin: {scope} ({count} vastaanottajaa).',
  'notice.banLifted': 'Porttikielto #{id} peruttiin.',
  'notice.roomRemovedResult': 'Huone #{room} poistettiin{members}.',
  'notice.userRemoved':
    '{nickname} poistettiin: {sessions} istunto(a) katkaistiin, {messages} viesti(ä) poistettiin.',
  'notice.roomRemovedMembers': ', {count} jäsentä poistettiin huoneesta',
  'notice.repeated': '{text} (×{count})',
  'notice.untilDate': '{date} asti',
  'notice.indefinitely': 'toistaiseksi',

  // ---------------------------------------------------------------- errors
  'error.generic': 'Jotain meni vikaan.',
  'error.notConnected': 'Ei yhteyttä.',
  'error.couldNotJoin': 'Huoneeseen #{room} ei voitu liittyä: {error}',
  'error.couldNotRejoin': 'Huoneeseen #{room} ei voitu liittyä uudelleen: {error}',
  'error.couldNotRestore': 'Huonetta #{room} ei voitu avata uudelleen: {error}',
  'error.couldNotStart': 'Ei voitu käynnistää ({source}): {error}',
  'error.couldNotPublish': 'Lähetys ei onnistunut ({source}): {error}',
  'error.couldNotRestart': 'Uudelleenkäynnistys ei onnistunut ({source}): {error}',
  'error.permissionDenied':
    'Lupa evätty. Selaimet sallivat kameran ja mikrofonin vain localhostissa tai HTTPS-yhteydellä.',
  'error.deviceGone': 'Valittua laitetta ei voitu käyttää ({device}). Valitse toinen asetuksista.',
  'error.deviceMicrophone': 'mikrofoni',
  'error.deviceCamera': 'kamera',
  'error.mediaFailed': 'Mediayhteys ({direction}) epäonnistui. Kytke laite uudelleen päälle.',
  'error.sessionEnded': 'Istuntosi päättyi. Kirjaudu uudelleen sisään.',
  'error.sessionRestoreFailed': 'Istuntoa ei voitu palauttaa: {error}',
  'error.sessionExpired': 'Istunto vanheni.',
  // ---- palvelimen avaimina lähettämät ilmoitukset
  'notice.userKicked': '{actor} potkaisi käyttäjän {nickname}.',
  'notice.userKickedReason': '{actor} potkaisi käyttäjän {nickname} ({reason}).',
  'notice.userBanned':
    '{actor} antoi käyttäjälle {nickname} porttikiellon ({where}) {expiry}{reasonText}{messagesText}.',
  'notice.forceStoppedOther': '{actor} sammutti käyttäjän {nickname} {kinds}.',
  'notice.youAreOperator': '{actor} antoi sinulle operaattorin oikeudet {scopeText}.',
  'notice.youAreNoLongerOperator': '{actor} poisti operaattorin oikeutesi {scopeText}.',
  'notice.operatorAddedRoom': '{nickname} on nyt operaattori huoneessa #{room}.',
  'notice.operatorRemovedRoom': '{nickname} ei ole enää operaattori huoneessa #{room}.',

  // ---------------------------------------------------------- tilastot
  'ui.stats.open': 'Tilastot',
  'ui.stats.title': 'Tilastot',
  'ui.stats.titleFor': 'Kanavan #{room} tilastot',
  'ui.stats.tab.activity': 'Aktiivisuus',
  'ui.stats.tab.talkers': 'Höpöttäjät',
  'ui.stats.tab.words': 'Sanat',
  'ui.stats.tab.media': 'Lähetykset',
  'ui.stats.tab.jukebox': 'Jukeboksi',
  'ui.stats.tab.fun': 'Huomionarvoista',
  'ui.stats.summary': 'Tällä {days} päivän ajanjaksolla kanavalla #{room}: {nicknames} nimimerkkiä, {lines} riviä.',
  'ui.stats.disabled': 'Tämä palvelin ei kerää kanavatilastoja.',
  'ui.stats.empty': 'Täältä ei ole vielä kirjattu mitään. Tilastot kertyvät kanavaa käytettäessä.',
  'ui.stats.nothingYet': 'Ei vielä kirjattua.',
  'ui.stats.nothingNotable': 'Mikään ei vielä erotu joukosta — palaa kun kanavalla on enemmän sanottavaa.',

  // ---- yhteiset sarakeotsikot
  'ui.stats.who': 'Kuka?',
  'ui.stats.what': 'Mitä?',
  'ui.stats.when': 'Milloin?',
  'ui.stats.lines': 'Rivien määrä',
  'ui.stats.words': 'Sanojen määrä',
  'ui.stats.word': 'Sana',
  'ui.stats.uses': 'Mainittu (kertaa)',
  'ui.stats.lastBy': 'Viimeksi maininnut',
  'ui.stats.mentions': 'Mainittu (kertaa)',
  'ui.stats.link': 'Linkki',
  'ui.stats.total': 'Yhteensä',
  'ui.stats.sessions': 'Kertoja',
  'ui.stats.duration': 'Kesto',
  'ui.stats.started': 'Alkoi',
  'ui.stats.length': 'Pituus',
  'ui.stats.added': 'Lisätty',
  'ui.stats.track': 'Kappale',
  'ui.stats.tracks': 'Kappaleita',
  'ui.stats.artist': 'Kanava',
  'ui.stats.timesQueued': 'Jonotettu (kertaa)',
  'ui.stats.wordsPerLine': 'Sanaa rivillä',
  'ui.stats.charsPerLine': 'Merkkiä rivillä',
  'ui.stats.lastSeen': 'Viimeksi paikalla',
  'ui.stats.today': 'Tänään',
  'ui.stats.yesterday': 'Eilen',
  'ui.stats.daysAgo': { one: '{count} päivä sitten', other: '{count} päivää sitten' },

  // ---- aktiivisuus
  'ui.stats.byHour': 'Vuorokauden aktiivisuuden aikajakauma',
  'ui.stats.byQuarter': 'Ahkerimmat kellonajan mukaan',
  'ui.stats.hourTitle': 'klo {hour} — {count} riviä',
  'ui.stats.peakHour': 'noin klo {hour}',
  'ui.stats.trend': 'Rivejä päivässä',
  'ui.stats.trendTitle': '{day} — {count} riviä',
  'ui.stats.trendBusiest': 'Vilkkain päivä {day}, {count} riviä',
  'ui.stats.trendAverage': 'keskimäärin {count} riviä päivässä',
  'ui.stats.trendQuiet': { one: '{count} päivä ilman puhetta', other: '{count} päivää ilman puhetta' },
  'ui.stats.trendAria':
    'Pylväskaavio: rivejä päivässä viimeisten {days} päivän ajalta, yhteensä {lines}, vilkkain {day} ({peak} riviä).',
  'ui.stats.showTable': 'Näytä luvut',
  'ui.stats.day': 'Päivä',
  'ui.stats.alsoRan': 'Seuraavat henkilöt eivät olleet niin ahkeria:',
  'ui.stats.omitted': {
    one: 'Tilastojen ulkopuolelle jäi {count} nimimerkki.',
    other: 'Tilastojen ulkopuolelle jäi {count} nimimerkkiä.',
  },

  // ---- sanat
  'ui.stats.topWords': 'Käytetyimpiä sanoja',
  'ui.stats.mostReferenced': 'Kaivatuimpia nimimerkkejä',
  'ui.stats.hotLinks': 'Kuumia linkkejä',

  // ---- lähetykset
  'ui.stats.totalAirtime': 'Lähetysaikaa yhteensä',
  'ui.stats.broadcasts': 'Lähetyksiä',
  'ui.stats.broadcasters': 'Lähettäjiä',
  'ui.stats.airtimeLeaders': 'Eniten lähetysaikaa',
  'ui.stats.recentBroadcasts': 'Viimeisimmät lähetykset',
  'ui.stats.noBroadcasts': 'Kukaan ei ole vielä lähettänyt täällä.',
  'ui.stats.live': 'Suora',
  'ui.stats.source.mic': 'Mikrofoni',
  'ui.stats.source.camera': 'Kamera',
  'ui.stats.source.screen': 'Näyttö',

  // ---- jukeboksi
  'ui.stats.tracksQueued': 'Kappaleita jonotettu',
  'ui.stats.uniqueTracks': 'Eri kappaleita',
  'ui.stats.totalMusic': 'Kestoa yhteensä',
  'ui.stats.jukeboxLeaders': 'Kuka jonottaa musiikin',
  'ui.stats.topTracks': 'Jonotetuimmat kappaleet',
  'ui.stats.recentTracks': 'Viimeksi jonotetut',
  'ui.stats.runningTime': 'Kestoa',
  'ui.stats.noTracks': 'Täällä ei ole vielä jonotettu mitään.',
  'ui.stats.unnamedTrack': 'nimi ei tiedossa',
  'ui.stats.unnamedTrackHint':
    'Video {videoId} — nimeä ei saatu selville jonotushetkellä. Palvelin yrittää uudelleen taustalla.',

  // ---- huomionarvoista ja kanavan keskiarvot
  'ui.stats.notable': 'Suuria lukuja',
  'ui.stats.channelAverages': 'Kanavan keskiarvot',
  'ui.stats.topicChanges': 'Otsikon vaihtoja',
  'ui.stats.recentTopics': 'Viimeisimmät otsikot',
  'ui.stats.topicBy': 'asettanut {nickname}, {when} klo {time}',

  /*
   * Yksi lause kutakin listaa kohti, toiselle sijalle oma muotoilunsa. Sävy
   * seuraa pisg-perinnettä, josta nämä on mallinnettu: kiusoittelevaa, ja
   * numeroista eikä ihmisestä.
   */
  'ui.stats.fun.questions': 'Onko {nickname} vähän pihalla vai muuten kyselee liikaa? {percent} hänen riveistään olivat kysymyksiä!',
  'ui.stats.fun.questions2': '{nickname} on myöskin utelias: {percent} hänen puheistaan olivat kysymyksiä.',
  'ui.stats.fun.yells': 'Eniten meistä kirkui {nickname}, tarkemmin ottaen {percent} ajasta!',
  'ui.stats.fun.yells2': 'Toinen temperamenttikko oli {nickname}, joka huusi {percent} ajasta.',
  'ui.stats.fun.caps': 'Näyttää siltä että {nickname} on unohtanut Caps Lockin päälle: peräti {percent} ajasta hän kirjoitti ISOILLA.',
  'ui.stats.fun.caps2': 'Kanavan toinen dementikko on {nickname}: Caps Lock päällä {percent} ajasta.',
  'ui.stats.fun.smiles': '{nickname} kirkastaa päiväsi: {percent} hänen lauseistaan sisälsivät hymiön.',
  'ui.stats.fun.smiles2': '{nickname} osaa iloita myös: hymiöitä piisaa {percent} ajasta.',
  'ui.stats.fun.frowns': '{nickname} on aina apeana: {percent} hänen lauseistaan sisälsivät surullisia kasvoja.',
  'ui.stats.fun.frowns2': '{nickname} oli myös surullinen {percent} ajasta.',
  'ui.stats.fun.swears': '{nickname} on kanavan sailori: {percent} hänen riveistään sisälsi kirosanan!',
  'ui.stats.fun.swears2': '{nickname} ei osaa myöskään hallita käytöstään: {percent} ajasta kiroilua.',
  'ui.stats.fun.slapsGiven': '{nickname} on todella hyökkäävä persoona! Hän pahoinpiteli muita peräti {value} kertaa!',
  'ui.stats.fun.slapsGiven2': '{nickname} on myös agressiivinen. Hän pahoinpiteli muita {value} kertaa.',
  'ui.stats.fun.slapsTaken': '{nickname} parka, kukaan ei pidä hänestä. Häntä kohdeltiin kaltoin peräti {value} kertaa!',
  'ui.stats.fun.slapsTaken2': '{nickname} näyttää olevan myös epäsuosiossa: häntä pahoinpideltiin {value} kertaa.',
  'ui.stats.fun.actions': '{nickname} halusi kaikkien tietävän mitä on tekemässä: {value} /me:tä!',
  'ui.stats.fun.actions2': 'Myöskin {nickname} osaa olla itserakas: selitteli tekemisiään {value} kertaa.',
  'ui.stats.fun.monologues': '{nickname} on puhunut paljon itsekseen: {value} monologia, pisin {longest} riviä.',
  'ui.stats.fun.monologues2': 'Toinen yksinäinen sielu on {nickname}, joka avautui {value} kertaa.',
  'ui.stats.fun.kicksTaken': '{nickname} sai kenkää ennätykselliset {value} kertaa!',
  'ui.stats.fun.kicksTaken2': '{nickname} seuraa perässä: saanut kenkää {value} kertaa.',
  'ui.stats.fun.kicksGiven': '{nickname} ei harrasta YT-neuvotteluja; hän antoi kenkää peräti {value} kertaa!',
  'ui.stats.fun.kicksGiven2': '{nickname} koitti niinikään pomottaa {value} potkulla.',
  'ui.stats.fun.opsGiven': '{nickname} jakeli kanavalla oppeja {value} kertaa!',
  'ui.stats.fun.opsGiven2': '{nickname} oli myös todella kohtelias: hän jakoi oppeja {value} kertaa.',
  'ui.stats.fun.deopsGiven': '{nickname} yritti olla pomo {value} deopilla.',
  'ui.stats.fun.deopsGiven2': 'Sijaispomoksi pyrkii {nickname}: nöyryyttänyt muita {value} kertaa.',
  'ui.stats.fun.joins': '{nickname} ei osaa päättää, ollako vaiko eikö olla: hän on liittynyt kanavalle {value} kertaa!',
  'ui.stats.fun.joins2': '{nickname} palaili myöskin ahkerasti, {value} kertaa.',
  'ui.stats.fun.uploads': '{nickname} toi kuvat mukanaan: {value} liitettä.',
  'ui.stats.fun.uploads2': '{nickname} jakoi niitä myös {value} kappaletta.',
  'ui.stats.fun.mostWords': '{nickname} on selvästi kanavan pikakirjoittaja {value} sanalla!',
  'ui.stats.fun.mostWords2': '{nickname} on hyvänä kakkosena {value} sanalla.',
  'ui.stats.fun.longestLines': '{nickname} naputteli ennätyksellisen pitkiä rivejä, keskimäärin {value} merkkiä riviä kohti.',
  'ui.stats.fun.longestLines2': '{nickname} venyttää rivejään myös: {value} merkkiä riviä kohti.',
  'ui.stats.fun.shortestLines': '{nickname} nauttii lyhyimpien lauseiden kirjoittelusta, hän kirjoitti vain {value} merkkiä riville.',
  'ui.stats.fun.shortestLines2': 'Myös {nickname} nauttii lyhyistä riveistä: vain {value} merkkiä riviä kohtaan.',
  'ui.stats.fun.mostWordsPerLine': '{nickname} kirjoitti keskimäärin {value} sanan lauseita.',
  'ui.stats.fun.mostWordsPerLine2': 'Myös {nickname} suosii pitkiä lauseita: {value} sanaa lausetta kohti.',

  // ---- kestot ja päättymisajat
  'expiry.permanent': 'pysyvästi',
  'expiry.expired': 'päättynyt',
  'expiry.for': '{duration} ajan',
  'duration.d': '{value} pv',
  'duration.h': '{value} t',
  'duration.m': '{value} min',
  'duration.s': '{value} s',
  // ---- mukana tulevan esimerkkilaajennuksen tekstit
  'plugin.welcome': 'Tervetuloa huoneeseen #{room}. Kirjoita /help nähdäksesi mitä voit tehdä.',
  'plugin.welcomeGuest':
    'Tervetuloa huoneeseen #{room}. Kirjoita /help nähdäksesi mitä voit tehdä, tai rekisteröi nimimerkkisi.',
};
